import { cn } from '@ais-ui/utils';
import {
  createClientWithAzure,
  createClientWithCredentials,
  getEmptySessions,
  LoginErrorReason,
  Sessions,
  User,
  ValidatedSessions,
  WebSockets,
} from './createSession';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Icons,
  Input,
  Label,
} from '@ais-ui/ui-core';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useAuth } from './AuthProvider';
import { useLocalization } from '@ais-ui/locale-management';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PiWindowsLogo } from 'react-icons/pi';
import { MultiFactorTOTPDialog } from '../../../../apps/ais/src/modules/MultiFactor/MultiFactorTOTPDialog';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../apps/ais/src/routes';
import { JSONRPCServerAndClient } from 'json-rpc-2.0';

const loginFormSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginFormValues = z.infer<typeof loginFormSchema>;

type UserAuthFormProps = React.HTMLAttributes<HTMLDivElement>;

type LoginSuccess = {
  success: true;
  data: {
    sessions: ValidatedSessions;
    user: User;
  };
};

type LoginError = {
  success: false;
  error: {
    sessions: Sessions;
    reason: LoginErrorReason;
  };
};

type LoginResult = LoginSuccess | LoginError;

async function loginWithWebsocketWithCredentials(
  credentials: {
    username: string;
    password: string;
  },
  webSockets: WebSockets,
  totpCode?: number,
  timeZone?: string
): Promise<LoginResult> {
  const { sessions, user, reason } = await createClientWithCredentials(
    credentials,
    webSockets,
    totpCode,
    timeZone
  );

  if (reason) {
    return {
      success: false,
      error: {
        sessions: sessions,
        reason: reason ? reason : 'MISSING_SESSION',
      },
    };
  }

  return {
    success: true,
    data: {
      sessions: sessions as ValidatedSessions,
      user,
    },
  };
}

async function loginWithWebsocketWithAzure(
  idToken: string,
  accessToken: string,
  webSockets: WebSockets,
  timeZone?: string
): Promise<false | { sessions: ValidatedSessions; user: User }> {
  const { sessions, user } = await createClientWithAzure(
    idToken,
    accessToken,
    webSockets,
    timeZone
  );

  if (!sessions.olSession || !sessions.sessionId) {
    return false;
  }
  return { sessions: sessions as ValidatedSessions, user };
}

export function LoginForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTOTPDialog, setShowTOTPDialog] = useState<boolean>(false);
  const [isLoadingAzure, setIsLoadingAzure] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [temporaryCredentials, setTemporaryCredentials] = useState<{
    username: string;
    password: string;
  }>({ username: '', password: '' });

  useEffect(() => {
    if (!showTOTPDialog) {
      setTemporaryCredentials({ username: '', password: '' });
    }
  }, [showTOTPDialog]);

  const { messages } = useLocalization();

  const {
    login,
    loginWithAzure,
    storeCredentials,
    setTwoFactorEnabled,
    initiateWebsockets,
    setSessions,
  } = useAuth();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  useEffect(() => {
    const handleConnectionError = (event: any) => {
      const websocketUrl = event.detail?.websocketUrl;
      const errorDescription = event.detail?.error;

      // Use the specific error if provided, otherwise fall back to generic message
      let errorMessage: string;
      if (errorDescription && websocketUrl) {
        errorMessage = `${errorDescription}: ${websocketUrl}`;
      } else if (websocketUrl) {
        errorMessage = `Cannot connect to WebSocket: ${websocketUrl}`;
      } else {
        errorMessage = 'Cannot connect to WebSocket';
      }

      setLoginError(errorMessage);
      setIsLoading(false);
    };

    window.addEventListener('sessionConnectionError', handleConnectionError);

    return () => {
      window.removeEventListener(
        'sessionConnectionError',
        handleConnectionError
      );
    };
  }, []);

  async function onCredentialsLogin(data: LoginFormValues, totpCode?: number) {
    setIsLoading(true);
    setLoginError(null);
    const username = data.username;
    const password = data.password;

    const webSockets = await initiateWebsockets();

    const result = await loginWithWebsocketWithCredentials(
      { username, password },
      webSockets,
      totpCode,
      'UTC' // Always use UTC for login
    );

    if (!result.success) {
      setSessions(result.error.sessions);
      if (result.error.reason === 'MISSING_TOTP_CODE') {
        setTemporaryCredentials({ username, password });
        setShowTOTPDialog(true);
        setIsLoading(false);
      } else if (result.error.reason === 'INVALID_TOTP_CODE') {
        setLoginError(messages.loginIncorrectTOTP);
        setIsLoading(false);
      } else if (result.error.reason === 'MFA_REQUIRED') {
        storeCredentials({ username, password });
        navigate(ROUTES.MULTIFACTOR.BEFORE_LOGIN.path);
      } else {
        setTemporaryCredentials({ username: '', password: '' });
        setLoginError(messages.loginIncorrectCredentials);
        setIsLoading(false);
      }
    } else {
      const { sessions, user } = result.data;
      storeCredentials({ username, password });
      if (totpCode) {
        setTwoFactorEnabled(true);
      }
      login(sessions, user);
    }
  }

  async function onAzureLogin() {
    setIsLoadingAzure(true);
    const tokens = await loginWithAzure();
    if (!tokens) {
      setLoginError(messages.loginIncorrectCredentialsAzure);
      setIsLoadingAzure(false);
      return;
    }

    const { idToken, accessToken } = tokens;

    const webSockets = await initiateWebsockets();

    const result = await loginWithWebsocketWithAzure(
      idToken,
      accessToken,
      webSockets,
      'UTC' // Always use UTC for login
    );

    if (!result) {
      setLoginError(messages.loginIncorrectCredentialsAzurePermissions);
      setIsLoadingAzure(false);
    } else {
      const { sessions, user } = result;
      login(sessions, user);
    }
  }

  return (
    <div className={cn('grid gap-4', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit((data) => onCredentialsLogin(data))}>
          <div className="grid gap-8">
            <div className="grid gap-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <Label className="sr-only mb-2" htmlFor="username">
                      {messages.loginUsername}
                    </Label>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        {...field}
                        id="username"
                        placeholder={messages.loginUsername}
                        type="text"
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="on"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <Label className="sr-only mb-2" htmlFor="password">
                      {messages.loginPassword}
                    </Label>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        {...field}
                        id="password"
                        placeholder={messages.loginPassword}
                        type="password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="on"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {loginError && !isLoading && (
              <Alert variant="destructive" className="login-alert">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{loginError}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={isLoading || isLoadingAzure}>
              {isLoading && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              )}
              {messages.loginSignIn}
            </Button>
          </div>
        </form>
      </Form>
      <Button
        variant="outline"
        disabled={isLoading || isLoadingAzure}
        onClick={onAzureLogin}
      >
        {isLoadingAzure && (
          <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
        )}
        <PiWindowsLogo className="h-6 w-6 mx-2"></PiWindowsLogo>
        {messages.loginSignInMicrosoft}
      </Button>
      <MultiFactorTOTPDialog
        showDialog={showTOTPDialog}
        setShowDialog={setShowTOTPDialog}
        onCredentialsLogin={onCredentialsLogin}
        loginError={loginError}
        credentials={temporaryCredentials}
      ></MultiFactorTOTPDialog>
    </div>
  );
}
